import { useEffect, useState } from 'react';

/**
 * useOnlineStatus — MOB-041 (Lot 4).
 *
 * Reflète `navigator.onLine`, mis à jour par les évènements natifs
 * `online`/`offline`. Aucune logique de synchronisation n'est ajoutée ici :
 * cette app ne fait pas de synchronisation de données en arrière-plan (voir
 * audit Lot 4) — la connectivité n'affecte que la validation de licence
 * (`useLicense`/`api.ts`) et la sauvegarde cloud (`cloudBackup.ts`), jamais
 * les données de travail (Dexie/localStorage, toujours locales).
 *
 * `navigator.onLine` peut renvoyer un faux positif (réseau local présent
 * mais pas d'accès Internet réel) — c'est une limitation connue de l'API,
 * pas de ce hook : on ne fabrique pas de vérification réseau supplémentaire
 * (ping, fetch de contrôle) qui n'existe pas ailleurs dans cette app.
 */
export function useOnlineStatus(): boolean {
  const [isOnline, setIsOnline] = useState<boolean>(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
}
